"use client";

import { c as _c } from "react/compiler-runtime";
import type { SelectedOptionInput } from "@shopify/hydrogen-react/storefront-api-types";
import { FragmentOfShopifyStorefront, VariablesOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { useMutation, useQuery } from "@tanstack/react-query";
import { deleteCookie, getCookie } from "cookies-next/client";
import { CART_QUERY_KEY } from "@repo/constants";
import { useSearchParams } from "next/navigation";
import { useEffect, useState, type Usable } from "react";
import type { ResolvedIntlConfig } from "react-intl";
import { toast } from "sonner";
import { Cart_Fragment, ProductOption_Fragment } from "#/graphql/shopify-storefront/fragments";
import { CustomerAccessTokenDelete_Mutation } from "#/graphql/shopify-storefront/mutations";
import { Cart_Query } from "#/graphql/shopify-storefront/queries";
import { getInContextVariables, fetcherShopifyStorefront, readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { useGetIntl } from "@repo/i18n/intl/client";
import { ProductVariantBySelectedOptions_Query } from "./graphql";
export function useCartId(initialCartId) {
  const $ = _c(4);
  let t0;
  if ($[0] !== initialCartId) {
    t0 = () => {
      if (initialCartId) {
        return initialCartId;
      }
      if (typeof window !== "undefined") {
        return getCookie("cartId") as string | undefined;
      }
    };
    $[0] = initialCartId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [cartId, setCartId] = useState(t0);
  const [prevInitialCartId, setPrevInitialCartId] = useState(initialCartId);
  if (initialCartId !== prevInitialCartId) {
    setPrevInitialCartId(initialCartId);
    setCartId(initialCartId);
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const syncCartId = () => {
        setCartId(getCookie("cartId") as string | undefined);
      };
      window.addEventListener("cart-updated", syncCartId);
      return () => {
        window.removeEventListener("cart-updated", syncCartId);
      };
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return cartId;
}
export function useCartQuery(initialCartId) {
  const $ = _c(12);
  const cartId = useCartId(initialCartId);
  const t0 = cartId || "";
  let t1;
  if ($[0] !== t0) {
    t1 = {
      cartId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const variables = t1;
  let t2;
  if ($[2] !== cartId) {
    t2 = [CART_QUERY_KEY, cartId];
    $[2] = cartId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queryKey = t2;
  const t3 = !!cartId;
  let t4;
  if ($[4] !== variables) {
    t4 = () => fetcherShopifyStorefront(Cart_Query, variables);
    $[4] = variables;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== queryKey || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      enabled: t3,
      queryKey,
      queryFn: t4,
      staleTime: 5000
    };
    $[6] = queryKey;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data
  } = useQuery(t5);
  if (!data) {
    return null;
  }
  const {
    cartMasked
  } = data;
  let t6;
  if ($[10] !== cartMasked) {
    t6 = readFragmentShopifyStorefront(Cart_Fragment, cartMasked);
    $[10] = cartMasked;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const cart = t6;
  return cart;
}
export function useCustomerAccessTokenDeleteMutation(t0) {
  const $ = _c(6);
  const {
    dictionaryPromise,
    lang
  } = t0;
  deleteCookie("customerAccessToken");
  const customerAccessTokenDeleteMutationFn = _temp;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["customerAccessTokenDelete"];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== intl) {
    t3 = {
      mutationFn: customerAccessTokenDeleteMutationFn,
      mutationKey: t2,
      onError: _temp2,
      onSuccess: (data, variables_1, context_0) => {
        console.debug("onSuccess", {
          data,
          variables: variables_1,
          context: context_0
        });
        toast.success(intl.formatMessage({
          defaultMessage: "Successfully signed out",
          id: "Q/BFnh",
          description: "Text for a successfully signed out status message in the GlobalNavbarContent component"
        }));
      }
    };
    $[4] = intl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mutate = useMutation(t3);
  return mutate;
}
function _temp2(error, variables_0, context) {
  console.error("onError", {
    error,
    variables: variables_0,
    context
  });
}
function _temp(variables) {
  return fetcherShopifyStorefront(CustomerAccessTokenDelete_Mutation, variables);
}
export function useProductVariantBySelectedOptionsQuery(t0) {
  const $ = _c(18);
  const {
    handle,
    lang,
    optionsMasked
  } = t0;
  const searchParams = useSearchParams();
  let selectedOptions;
  let t1;
  if ($[0] !== optionsMasked || $[1] !== searchParams) {
    selectedOptions = Array.from(searchParams.entries()).map(_temp3);
    t1 = selectedOptions.every(selectedOption => {
      const existing = optionsMasked.map(_temp4).find(option => {
        if (option.name.toLocaleLowerCase() !== selectedOption.name.toLocaleLowerCase()) {
          return false;
        }
        if (!option.optionValues.map(_temp5).includes(selectedOption.value.toLocaleLowerCase())) {
          return false;
        }
        return true;
      });
      return Boolean(existing);
    });
    $[0] = optionsMasked;
    $[1] = searchParams;
    $[2] = selectedOptions;
    $[3] = t1;
  } else {
    selectedOptions = $[2];
    t1 = $[3];
  }
  const hasAllSelectedOptions = t1;
  if (!hasAllSelectedOptions) {
    console.error(`Not all selected options are valid for the product with handle "${handle}". Please check the options provided.`);
  }
  let t2;
  if ($[4] !== lang) {
    t2 = getInContextVariables(lang);
    $[4] = lang;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const inContextVariables = t2;
  let t3;
  if ($[6] !== handle || $[7] !== inContextVariables || $[8] !== selectedOptions) {
    t3 = {
      ...inContextVariables,
      handle,
      selectedOptions
    };
    $[6] = handle;
    $[7] = inContextVariables;
    $[8] = selectedOptions;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const variables = t3;
  let t4;
  if ($[10] !== variables) {
    t4 = [ProductVariantBySelectedOptions_Query, variables];
    $[10] = variables;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const queryKey = t4;
  let t5;
  if ($[12] !== inContextVariables || $[13] !== variables) {
    t5 = () => fetcherShopifyStorefront(ProductVariantBySelectedOptions_Query, {
      ...inContextVariables,
      ...variables
    });
    $[12] = inContextVariables;
    $[13] = variables;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== queryKey || $[16] !== t5) {
    t6 = {
      queryKey,
      queryFn: t5
    };
    $[15] = queryKey;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const {
    data
  } = useQuery(t6);
  return data;
}
function _temp5(optionValue) {
  return optionValue.name.toLocaleLowerCase();
}
function _temp4(productOptionMasked) {
  return readFragmentShopifyStorefront(ProductOption_Fragment, productOptionMasked);
}
function _temp3(t0) {
  const [name, value] = t0;
  return {
    name,
    value
  };
}